import type { AppUserWithStatusEvent } from "@shared/schema";
import { useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ExternalLink, Loader2, Send } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CrispConversationsResponse, CrispMessagesResponse } from "./types";

export function CrispConversationsDialog({
  open,
  user,
  onClose,
}: {
  open: boolean;
  user: AppUserWithStatusEvent | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const email = user?.email ?? "";
  const label = user?.fullName || user?.name || user?.email || "this host";
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [reply, setReply] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    setSelectedSession(null);
  }, [email, open]);

  useEffect(() => {
    setReply("");
  }, [selectedSession]);

  // Explicit queryFn: the default fetcher joins the key with "/", but this
  // route takes the host email as a query param, not a path segment.
  const convQuery = useQuery<CrispConversationsResponse>({
    queryKey: ["/api/admin/crisp/conversations", email],
    enabled: open && !!email,
    queryFn: async () => {
      const res = await fetch(
        `/api/admin/crisp/conversations?email=${encodeURIComponent(email)}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
  });

  const messagesQuery = useQuery<CrispMessagesResponse>({
    queryKey: ["/api/admin/crisp/conversations", selectedSession, "messages"],
    enabled: open && !!selectedSession,
  });

  const conversations = convQuery.data?.conversations ?? [];
  const selected = conversations.find((c) => c.sessionId === selectedSession) ?? null;

  const sendMutation = useMutation({
    mutationFn: async (content: string) => {
      if (!selectedSession) throw new Error("No conversation selected");
      return apiRequest(
        "POST",
        `/api/admin/crisp/conversations/${selectedSession}/message`,
        { content },
      );
    },
    onSuccess: () => {
      setReply("");
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/crisp/conversations", selectedSession, "messages"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/crisp/conversations", email],
      });
    },
    onError: (err) => {
      toast({
        title: "Failed to send reply",
        description: parseErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    },
  });

  const stateMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (state: string) => {
      if (!selectedSession) throw new Error("No conversation selected");
      return apiRequest(
        "PATCH",
        `/api/admin/crisp/conversations/${selectedSession}/state`,
        { state },
      );
    },
    onSuccess: (_data, state) => {
      toast({ title: `Conversation marked ${state}` });
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/crisp/conversations", email],
      });
    },
    onError: (err) => {
      toast({
        title: "Failed to change status",
        description: parseErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    },
  });

  function fmtTs(ts: number | null): string {
    if (!ts) return "—";
    try {
      return new Date(ts).toLocaleString();
    } catch {
      return "—";
    }
  }

  function stateVariant(state: string): "default" | "secondary" | "destructive" | "outline" {
    if (state === "resolved") return "secondary";
    if (state === "pending") return "destructive";
    if (state === "unresolved") return "default";
    return "outline";
  }

  const busy = sendMutation.isPending || stateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && busy) return; if (!o) onClose(); }}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="dialog-crisp-conversations"
        onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
        onInteractOutside={(e) => { if (busy) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (busy) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>Support conversations</DialogTitle>
          <DialogDescription>
            Read-only Crisp chat history for <strong>{label}</strong> ({email || "no email"}).
            {convQuery.data?.inboxUrl && (
              <>
                {" "}
                <a
                  href={convQuery.data.inboxUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-brand hover:underline"
                  data-testid="link-crisp-inbox"
                >
                  Open Crisp inbox <ExternalLink className="h-3 w-3" />
                </a>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {convQuery.isLoading ? (
          <div className="flex items-center justify-center py-12" data-testid="status-crisp-loading">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : convQuery.isError ? (
          <p className="py-8 text-sm text-destructive" data-testid="text-crisp-error">
            {parseErrorMessage(convQuery.error, "Failed to load conversations.")}
          </p>
        ) : convQuery.data && !convQuery.data.configured ? (
          <p className="py-8 text-sm text-muted-foreground" data-testid="text-crisp-not-configured">
            Crisp is not configured on this environment (missing API credentials).
          </p>
        ) : convQuery.data && !convQuery.data.found ? (
          <p className="py-8 text-sm text-muted-foreground" data-testid="text-crisp-no-profile">
            No Crisp customer record exists for this email yet — the host hasn't started a chat.
          </p>
        ) : conversations.length === 0 ? (
          <p className="py-8 text-sm text-muted-foreground" data-testid="text-crisp-empty">
            This host has a Crisp profile but no conversation threads.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 overflow-hidden flex-1 min-h-[300px]">
            {/* Conversation list */}
            <div className="overflow-y-auto pr-1 space-y-2" data-testid="list-crisp-conversations">
              {conversations.map((c) => (
                <button
                  key={c.sessionId}
                  onClick={() => setSelectedSession(c.sessionId)}
                  className={`w-full text-left rounded-md border p-3 text-sm transition-colors hover:bg-accent ${
                    selectedSession === c.sessionId ? "border-brand bg-accent" : ""
                  }`}
                  data-testid={`item-crisp-conversation-${c.sessionId}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium truncate">{c.nickname || c.email || "Visitor"}</span>
                    <Badge variant={stateVariant(c.state)} className="shrink-0 text-[10px]">
                      {c.state}
                    </Badge>
                  </div>
                  <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                    {c.lastMessage || "No messages"}
                  </div>
                  <div className="mt-1 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                    <span>{fmtTs(c.updatedAt)}</span>
                    {c.unreadOperator > 0 && (
                      <Badge variant="destructive" className="text-[10px]">
                        {c.unreadOperator} unread
                      </Badge>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {/* Selected thread */}
            <div className="flex flex-col border rounded-md bg-muted/30 overflow-hidden" data-testid="panel-crisp-thread">
              {!selected ? (
                <p className="text-sm text-muted-foreground p-3" data-testid="text-crisp-thread-placeholder">
                  Select a conversation to read the full thread.
                </p>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-2 p-3 pb-2 border-b shrink-0">
                    <span className="text-sm font-medium truncate">
                      {selected.nickname || selected.email || "Conversation"}
                    </span>
                    <a
                      href={selected.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-brand hover:underline shrink-0"
                      data-testid="link-crisp-open-conversation"
                    >
                      Open in Crisp <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                  <div className="flex-1 overflow-y-auto p-3">
                    {messagesQuery.isLoading ? (
                      <div className="flex items-center justify-center py-8" data-testid="status-crisp-messages-loading">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </div>
                    ) : messagesQuery.isError ? (
                      <p className="text-sm text-destructive" data-testid="text-crisp-messages-error">
                        {parseErrorMessage(messagesQuery.error, "Failed to load messages.")}
                      </p>
                    ) : (messagesQuery.data?.messages.length ?? 0) === 0 ? (
                      <p className="text-sm text-muted-foreground" data-testid="text-crisp-messages-empty">
                        No messages in this conversation.
                      </p>
                    ) : (
                      <ol className="space-y-3" data-testid="list-crisp-messages">
                        {messagesQuery.data?.messages.map((m, i) => {
                          const isOperator = m.from === "operator";
                          return (
                            <li
                              key={`${m.fingerprint}-${i}`}
                              className={`rounded-md border p-2 text-sm ${
                                isOperator ? "bg-brand-muted ml-6" : "bg-background mr-6"
                              }`}
                              data-testid={`item-crisp-message-${i}`}
                            >
                              <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                                <span className="font-medium">
                                  {m.authorNickname || (isOperator ? "Operator" : "Visitor")}
                                </span>
                                <span>{fmtTs(m.timestamp)}</span>
                              </div>
                              <div className="mt-1 whitespace-pre-wrap break-words">
                                {m.type === "text"
                                  ? m.content || "—"
                                  : `[${m.type}]${m.content ? ` ${m.content}` : ""}`}
                              </div>
                            </li>
                          );
                        })}
                      </ol>
                    )}
                  </div>

                  {/* Composer + state controls */}
                  <div className="shrink-0 border-t p-3 space-y-2 bg-background/50">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] text-muted-foreground">Status:</span>
                      {(["unresolved", "pending", "resolved"] as const).map((s) => (
                        <Button
                          key={s}
                          type="button"
                          size="sm"
                          variant={selected.state === s ? "default" : "outline"}
                          className="h-7 text-xs capitalize"
                          disabled={stateMutation.isPending || selected.state === s}
                          onClick={() => stateMutation.mutate(s)}
                          data-testid={`button-crisp-state-${s}`}
                        >
                          {stateMutation.isPending && stateMutation.variables === s ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            s
                          )}
                        </Button>
                      ))}
                    </div>
                    <Textarea
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      placeholder="Type a reply to send to the customer…"
                      rows={3}
                      maxLength={4000}
                      className="resize-none text-sm"
                      disabled={sendMutation.isPending}
                      onKeyDown={(e) => {
                        if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && reply.trim()) {
                          e.preventDefault();
                          sendMutation.mutate(reply.trim());
                        }
                      }}
                      data-testid="input-crisp-reply"
                    />
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] text-muted-foreground">
                        Sends as an operator reply via Crisp. ⌘/Ctrl+Enter to send.
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        className="h-8"
                        disabled={sendMutation.isPending || !reply.trim()}
                        onClick={() => sendMutation.mutate(reply.trim())}
                        data-testid="button-crisp-send-reply"
                      >
                        {sendMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-1" /> Send
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="button-close-crisp-conversations">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
